import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover } from '#pplx/frontend/aether/components/Popover/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { createGovernmentSubscription } from '#pplx/frontend/apps/ask/queries/billing-queries/payment/createGovernmentSubscription.js';
import { useUserPromotionsQuery } from '#pplx/frontend/apps/ask/subscription/hooks/useUserPromotionsQuery.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import Button from '#pplx/frontend/components/buttons/Button.js';
import { CountryFlag } from '#pplx/frontend/components/CountryFlag.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useCueImpressionLimit } from '#pplx/frontend/upsells/useCueImpressionLimit.js';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const GOV_ONE_YEAR_FREE = 'gov_free_year';
const GOV_CODE = 'gov';
export const SidebarGovBadge = () => {
    const reason = 'sidebar-gov-badge';
    const { $t } = useIntl();
    const isUserLoggedIn = useIsUserLoggedIn();
    const { hasAccessToProFeatures } = useIsSubscribed();
    const userPromotionCampaign = useUserPromotionsQuery({ reason });
    const router = useRouter();
    const [isDismissed, setIsDismissed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { shouldShow } = useCueImpressionLimit({
        cueKey: 'gov-badge',
        impressionsLimit: 3,
        enabled: true,
    });
    // Check if user qualifies for gov free promo (same logic as AppBanner)
    const isGovOneYearFree = userPromotionCampaign.campaignId === GOV_ONE_YEAR_FREE;
    const qualifiesForGovPromo = isGovOneYearFree && !hasAccessToProFeatures;
    // Show activate button only if user is logged in and qualifies
    const showActivateButton = isUserLoggedIn && qualifiesForGovPromo;
    // While the cue impression is active the popover is forced open; once the
    // user dismisses it (outside click / Escape / trigger click) it reverts to a
    // plain hover popover.
    const isForcedOpen = shouldShow && !isDismissed;
    const onForcedOpenChange = useCallback((open) => {
        if (!open) {
            setIsDismissed(true);
        }
    }, []);
    const handleActivateClick = useCallback(async () => {
        if (isLoading)
            return;
        setIsLoading(true);
        try {
            const response = await createGovernmentSubscription({
                reason: 'sidebar-gov-badge-activation',
            });
            if (response?.status === 'success') {
                // Redirect to success page (welcome page for gov partner) - matches original success behavior
                router.push(`/join/p/${GOV_CODE}/welcome`);
            }
            else {
                // Handle error case - use existing error UI
                const errorCode = response?.error_code || 'UNKNOWN_ERROR';
                router.push(`/join/p/${GOV_CODE}?error=${errorCode}`);
            }
        }
        catch (_error) {
            // Handle network or other errors
            router.push(`/join/p/${GOV_CODE}?error=UNKNOWN_ERROR`);
        }
        finally {
            setIsLoading(false);
        }
    }, [router, isLoading]);
    const triggerElement = (_jsx("span", { className: "inline-flex translate-y-2 items-center justify-center", children: _jsxs(BoxPrimitive, { display: "flex", align: "center", justify: "center", gap: "xs", bg: "inverse", className: "px-2 ring-soft relative cursor-pointer py-1 ring-1 w-full", children: [_jsx(CountryFlag, { countryCode: "US", className: "relative w-3.5" }), _jsx(TextPrimitive, { variant: "tiny", color: "defaultInverted", className: "translate-y-0 relative", children: "GOV" })] }) }));
    const popoverContent = (_jsxs("div", { className: "gap-4 max-w-sideBarWidthXL flex flex-col", children: [_jsx("img", { src: "/static/images/us-gov.png", alt: $t({
                    defaultMessage: 'GOV',
                    id: '50e5792c20',
                    description: 'Alt text for government badge image in the sidebar',
                }), className: "h-14 w-full rounded-md object-cover" }), _jsxs("div", { children: [_jsx(TextPrimitive, { leading: "tight", variant: "baseSemi", className: "mb-1", children: _jsx(FormattedMessage, { defaultMessage: "Thank you for your service!", description: "Heading in the government badge popover thanking government users", id: "8bc7f40eea" }) }), _jsx(TextPrimitive, { leading: "snug", variant: "small", color: "light", children: showActivateButton
                            ? $t({
                                defaultMessage: 'Government accounts include Pro, premium models, and zero data usage.',
                                description: 'Description in the government badge popover for users eligible to activate Pro',
                                id: 'eef1c9389a',
                            })
                            : isUserLoggedIn
                                ? $t({
                                    defaultMessage: 'Government accounts include premium models and zero data usage.',
                                    description: 'Description in the government badge popover for logged-in government users who already have Pro',
                                    id: '7fd1dc4059',
                                })
                                : $t({
                                    defaultMessage: 'Government visitors get premium models and zero data usage.',
                                    description: 'Description in the government badge popover for visitors who are not logged in',
                                    id: 'a3afcfffde',
                                }) })] }), _jsxs("div", { className: "gap-2 flex flex-col", children: [showActivateButton && (_jsx(Button, { variant: "primary", size: "small", text: isLoading
                            ? $t({
                                defaultMessage: 'Activating...',
                                description: 'Loading state text on the activate Pro button in the government badge popover',
                                id: '66af0b69ec',
                            })
                            : $t({
                                defaultMessage: 'Activate Pro',
                                description: 'Button in the government badge popover to activate the free Pro subscription',
                                id: '6aec54619e',
                            }), onClick: handleActivateClick, disabled: isLoading })), _jsx(Button, { variant: "common", size: "small", text: $t({
                            defaultMessage: 'Learn more',
                            description: 'Button in the government badge popover to open the blog post about Perplexity for Government',
                            id: '0ea66b3d9c',
                        }), onClick: () => window.open('https://www.perplexity.ai/hub/blog/introducing-perplexity-for-government', '_blank') })] })] }));
    return isForcedOpen ? (_jsx(Popover, { open: true, onOpenChange: onForcedOpenChange, side: "right", offsetPx: 5, appearance: "dark", shouldShowArrow: true, 
        // The cue opens from impression state, not a user click; suppress
        // Radix's open auto-focus so mounting does not move focus (legacy
        // overlay parity).
        shouldAutoFocus: false, triggerElement: triggerElement, children: popoverContent })) : (_jsx(Popover, { interaction: "hover", openDelayMs: 0, side: "right", offsetPx: 5, appearance: "dark", shouldShowArrow: true, triggerElement: triggerElement, children: popoverContent }));
};
//# sourceMappingURL=SidebarGovBadge.js.map